import * as GAME from 'vibegame';
import { Transform, Parent } from 'vibegame/transforms';
import { Renderer } from 'vibegame/rendering';
import { PositionShaker } from '@blog-components/plugins';
import { TextGeneration } from './intro';

export const TextOutputParticle = GAME.defineComponent({
  index: GAME.Types.ui8,
  baseX: GAME.Types.f32,
  baseY: GAME.Types.f32,
  baseZ: GAME.Types.f32,
  randomScale: GAME.Types.f32,
  randomOffsetY: GAME.Types.f32,
});

export const TextOutputManager = GAME.defineComponent({
  initialized: GAME.Types.ui8,
  particleCount: GAME.Types.ui8,
  spacing: GAME.Types.f32,
  startX: GAME.Types.f32,
  tokenScaleT: GAME.Types.f32,
  flashT: GAME.Types.f32,
});

const PARTICLE_COUNT = 8;
const OUTPUT_Y_POSITIONS = [0];

const RANDOM_SCALES = [0.7, 1.3, 0.5, 1.1, 0.8, 1.4, 0.6, 1.0];
const RANDOM_OFFSETS = [1.8, -2.2, 2.5, -1.5, 2.2, -2.8, 1.5, -2.0];
const FLASH_TIMES = [0.1, 0.35, 0.55, 0.2, 0.7, 0.45, 0.8, 0.15];
const BASE_COLOR = 0xaa4488;

const managerQuery = GAME.defineQuery([TextOutputManager]);
const particleQuery = GAME.defineQuery([TextOutputParticle, Transform, Renderer]);
const textGenQuery = GAME.defineQuery([TextGeneration]);

const TextOutputSetupSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of managerQuery(state.world)) {
      if (TextOutputManager.initialized[eid]) continue;
      TextOutputManager.initialized[eid] = 1;

      const spacing = TextOutputManager.spacing[eid] || 1.5;
      const startX = TextOutputManager.startX[eid] || 11;

      for (let row = 0; row < OUTPUT_Y_POSITIONS.length; row++) {
        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const particle = state.createEntity();
          const baseX = startX + i * spacing;
          const baseY = OUTPUT_Y_POSITIONS[row];

          state.addComponent(particle, Transform, {
            posX: baseX,
            posY: baseY,
            posZ: 0,
            scaleX: 0,
            scaleY: 0,
            scaleZ: 0,
          });

          state.addComponent(particle, Renderer, {
            shape: 1,
            sizeX: 1.0,
            sizeY: 1.0,
            sizeZ: 1.0,
            color: 0xaa4488,
            visible: 1,
          });

          state.addComponent(particle, TextOutputParticle, {
            index: row * PARTICLE_COUNT + i,
            baseX,
            baseY,
            baseZ: 0,
            randomScale: RANDOM_SCALES[i % RANDOM_SCALES.length],
            randomOffsetY: RANDOM_OFFSETS[i % RANDOM_OFFSETS.length],
          });

          state.addComponent(particle, Parent, { entity: eid });
          state.addComponent(particle, PositionShaker);
        }
      }
    }
  },
};

const TextOutputAnimationSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    let textGenT = 0;
    let pulsePhase = 0;
    let tokenScaleT = 0;
    let flashT = 0;
    for (const eid of textGenQuery(state.world)) {
      textGenT = TextGeneration.t[eid];
      pulsePhase = TextGeneration.pulsePhase[eid];
    }
    for (const eid of managerQuery(state.world)) {
      tokenScaleT = TextOutputManager.tokenScaleT[eid];
      flashT = TextOutputManager.flashT[eid];
    }

    for (const eid of particleQuery(state.world)) {
      const index = TextOutputParticle.index[eid];
      const particleIndex = index % PARTICLE_COUNT;

      const appearDelay = particleIndex * 0.08;
      const particleT = Math.max(0, Math.min(1, (textGenT - appearDelay) / 0.3));

      const baseScale = particleT * particleT * (3 - 2 * particleT);
      const randomScale = TextOutputParticle.randomScale[eid];
      const scaleMult = 1 + (randomScale - 1) * tokenScaleT;
      const scale = baseScale * scaleMult;
      Transform.scaleX[eid] = scale;
      Transform.scaleY[eid] = scale;
      Transform.scaleZ[eid] = scale;

      const randomOffsetY = TextOutputParticle.randomOffsetY[eid];
      const offsetY = randomOffsetY * tokenScaleT;

      if (textGenT > 0.5) {
        const phase = pulsePhase + particleIndex * 0.5;
        const pulseAmplitude = 0.1 + tokenScaleT * 0.4;
        Transform.posY[eid] =
          TextOutputParticle.baseY[eid] + Math.sin(phase) * pulseAmplitude + offsetY;
      } else {
        Transform.posY[eid] = TextOutputParticle.baseY[eid] + offsetY;
      }

      const flashTime = FLASH_TIMES[particleIndex];
      const flashDist = Math.abs(flashT - flashTime);
      const isFlashing = flashDist < 0.08;
      Renderer.color[eid] = isFlashing ? 0xffffff : BASE_COLOR;
    }
  },
};

export const TextOutputPlugin: GAME.Plugin = {
  components: { TextOutputParticle, TextOutputManager },
  systems: [TextOutputSetupSystem, TextOutputAnimationSystem],
  config: {
    defaults: {
      'text-output-manager': {
        initialized: 0,
        particleCount: PARTICLE_COUNT,
        spacing: 2.5,
        startX: 11,
        tokenScaleT: 0,
        flashT: 0,
      },
    },
  },
};
